import { GetterTree, MutationTree, ActionTree, ActionContext, Module } from 'vuex';
import { KycOrgConfig } from '@/api/auth';
import { sendLog } from '@/api/client-logs';
import {
  isSupportedOSVersion,
  isSupportedOSType,
  isSupportedBrowser,
  isCameraUsable,
  checkInAppBrowser
} from '@/util/device';
import { RootState } from '../types';

export type State = {
  isReady: boolean;
  isOS: boolean;
  isBrowser: boolean;
  isCamera: boolean;
};

function createState(): State {
  const state: State = {} as State;
  clearState(state);
  return state;
}

function clearState(state: State): void {
  state.isReady = false;
  state.isOS = false;
  state.isBrowser = false;
  state.isCamera = false;
}

const getters: GetterTree<State, RootState> = {
  isReady: (state: State): boolean => state.isReady,
  isOS: (state: State): boolean => state.isOS,
  isBrowser: (state: State): boolean => state.isBrowser,
  isCamera: (state: State): boolean => state.isCamera,
  isSupport: (state: State): boolean => state.isOS && state.isBrowser && state.isCamera
};

const mutations: MutationTree<State> = {
  setReady(state: State): void {
    state.isReady = true;
  },

  setIsOs(state: State, payload: { isOS: boolean }): void {
    state.isOS = payload.isOS;
  },

  setIsBrowser(state: State, payload: { isBrowser: boolean }): void {
    state.isBrowser = payload.isBrowser;
  },

  setIsCamera(state: State, payload: { isCamera: boolean }): void {
    state.isCamera = payload.isCamera;
  },

  clearState(state: State): void {
    clearState(state);
  }
};

const actions: ActionTree<State, RootState> = {
  clearState(context: ActionContext<State, RootState>): void {
    context.commit('clearState');
  },

  check(context: ActionContext<State, RootState>): void {
    if (context.state.isReady) return;

    let isOS: boolean = isSupportedOSType() && isSupportedOSVersion();
    const isPC: boolean = context.rootGetters['camera/isPC'];
    const isEnablePC: boolean = context.rootGetters['auth/kycOrgConfigEnableClientTypePC'];
    if (isPC && !isEnablePC) isOS = false;

    const isBrowser: boolean = isSupportedBrowser();
    const isCamera: boolean = isCameraUsable();
    context.commit('setIsOs', { isOS });
    context.commit('setIsBrowser', { isBrowser });
    context.commit('setIsCamera', { isCamera });

    const errorList: string[] = [];
    if (!isOS) errorList.push('os');
    if (!isBrowser) errorList.push('browser');
    if (!isCamera) errorList.push('camera');
    //if (false) errorList.push('other');

    const inapp: string | null = checkInAppBrowser();
    if (!isBrowser && inapp !== null) errorList.push(inapp);

    if (errorList.length > 0) sendLog({ level: 'INFO', kind: 'device_unsupported', fields: errorList });

    context.commit('setReady');
  }
};

export const support: Module<State, RootState> = {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions
};
